/**
 * Lead solver (spec §6): where to aim so a projectile fired from a moving shooter meets a target that
 * keeps a constant velocity. Float64 throughout (D-011); allocation-free.
 */

export interface LeadSolution {
  /** Projectile flight time to the intercept (s). */
  t: number
  /**
   * Aim point, float64 world: `target + (targetVel − shooterVel) × t`. The projectile inherits the
   * shooter's velocity, so pointing the muzzle at this point sends it to the intercept; this is also
   * where the HUD lead indicator is drawn. The world intercept itself is `aim + shooterVel × t`.
   */
  x: number
  y: number
  z: number
}

/**
 * A class, not a literal (D-014): a `{ t, x, y, z }` literal shares its hidden-class transitions with every other
 * literal of those keys, so a field can read as tagged and each float written to it allocates a heap number.
 * A class has its own map, so the float fields stay unboxed and are written in place.
 */
class Lead implements LeadSolution {
  t = 0
  x = 0
  y = 0
  z = 0
}

export function createLeadSolution(): LeadSolution {
  return new Lead()
}

/**
 * `solveLeadWith`'s arguments: shooter position and velocity, target position and velocity, projectile speed.
 * A class, not a literal, so its float fields stay unboxed (the `SteerArgs` pattern, gate-A decision).
 */
export class LeadArgs {
  sx = 0
  sy = 0
  sz = 0
  svx = 0
  svy = 0
  svz = 0
  tx = 0
  ty = 0
  tz = 0
  tvx = 0
  tvy = 0
  tvz = 0
  speed = 0
}

const EPS = 1e-9

/**
 * `solveLead` with its 13 floats in `p` (D-014). A float passed to a call V8 does not inline is boxed, one heap number
 * each, per call: `solveLead` is past the inlining budget of an AI pilot's `stepEngage`, which paid ~200 B per pilot
 * per tick that way. A hot caller that is not inlined fills its own `LeadArgs` and calls this; the solve lives here.
 */
export function solveLeadWith(p: LeadArgs, out: LeadSolution): boolean {
  const sx = p.sx
  const sy = p.sy
  const sz = p.sz
  const tx = p.tx
  const ty = p.ty
  const tz = p.tz
  const speed = p.speed
  const rx = tx - sx
  const ry = ty - sy
  const rz = tz - sz
  const vx = p.tvx - p.svx
  const vy = p.tvy - p.svy
  const vz = p.tvz - p.svz
  const c = rx * rx + ry * ry + rz * rz
  const b = 2 * (rx * vx + ry * vy + rz * vz)
  const a = vx * vx + vy * vy + vz * vz - speed * speed

  let t = -1
  if (speed > 0) {
    if (Math.abs(a) < EPS) {
      // Target speed equals projectile speed: linear case b·t + c = 0.
      if (b < 0) t = -c / b
    } else {
      const disc = b * b - 4 * a * c
      if (disc >= 0) {
        const sq = Math.sqrt(disc)
        const t1 = (-b - sq) / (2 * a)
        const t2 = (-b + sq) / (2 * a)
        const lo = t1 < t2 ? t1 : t2
        const hi = t1 < t2 ? t2 : t1
        t = lo > 0 ? lo : hi > 0 ? hi : -1
      }
    }
  }

  const solved = t > 0
  if (!solved) t = speed > 0 ? Math.sqrt(c) / speed : 0
  out.t = t
  out.x = tx + vx * t
  out.y = ty + vy * t
  out.z = tz + vz * t
  return solved
}

const leadScratch = new LeadArgs()

/**
 * Solves |r + v·t| = s·t for the smallest positive t, with r = target − shooter and v = targetVel − shooterVel
 * (the closed-form quadratic). Returns true when solved. Otherwise (target outrunning the projectile, or
 * speed ≤ 0) it falls back to t = range ÷ speed, still writes the aim point and returns false.
 * Fills a module `LeadArgs` and calls `solveLeadWith` (same arithmetic, bit for bit).
 */
export function solveLead(
  sx: number,
  sy: number,
  sz: number,
  svx: number,
  svy: number,
  svz: number,
  tx: number,
  ty: number,
  tz: number,
  tvx: number,
  tvy: number,
  tvz: number,
  speed: number,
  out: LeadSolution,
): boolean {
  const p = leadScratch
  p.sx = sx
  p.sy = sy
  p.sz = sz
  p.svx = svx
  p.svy = svy
  p.svz = svz
  p.tx = tx
  p.ty = ty
  p.tz = tz
  p.tvx = tvx
  p.tvy = tvy
  p.tvz = tvz
  p.speed = speed
  return solveLeadWith(p, out)
}
